import React, { useState } from "react";
import {
  Check,
  Copy,
  Sparkles,
  Zap,
  TriangleAlert,
  BookOpen,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import styles from "../../styles/docs/docsContent.module.scss";
import glossaryStyles from "../../styles/docs/docsGlossary.module.scss";

const INFO_BOX_VARIANTS = {
  info: {
    boxClass: styles.infoBoxInfo,
    iconClass: styles.infoIcon,
    icon: Sparkles,
  },
  tip: {
    boxClass: styles.infoBoxTip,
    iconClass: styles.tipIcon,
    icon: Zap,
  },
  warning: {
    boxClass: styles.infoBoxWarning,
    iconClass: styles.warningIcon,
    icon: TriangleAlert,
  },
  note: {
    boxClass: styles.infoBoxNote,
    iconClass: styles.noteIcon,
    icon: BookOpen,
  },
};

const BADGE_VARIANTS = {
  blue: styles.badgeBlue,
  green: styles.badgeGreen,
  amber: styles.badgeAmber,
  slate: styles.badgeSlate,
  red: styles.badgeRed,
  violet: styles.badgeViolet,
};

const METHOD_BADGE_VARIANTS = {
  GET: styles.methodGet,
  POST: styles.methodPost,
  PUT: styles.methodPut,
  PATCH: styles.methodPatch,
  DELETE: styles.methodDelete,
};

export const Strong = ({ children }) => (
  <strong className={styles.strong}>{children}</strong>
);

export const GlossaryTerm = ({ icon: Icon, term, def }) => (
  <div className={glossaryStyles.termRow}>
    <div className={glossaryStyles.termIconWrap}>
      <Icon size={14} className={glossaryStyles.termIcon} />
    </div>
    <div className={glossaryStyles.termBody}>
      <span className={glossaryStyles.termTitle}>{term}</span>
      <p className={glossaryStyles.termDef}>{def}</p>
    </div>
  </div>
);

export const InlineCode = ({ children, variant }) => (
  <code className={variant === "plain" ? styles.inlineCodePlain : styles.inlineCode}>
    {children}
  </code>
);

export const CodeBlock = ({ code, language = "bash" }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className={styles.codeBlock}>
      <div className={styles.codeHeader}>
        <span className={styles.codeLang}>{language}</span>
        <button type="button" onClick={handleCopy} className={styles.codeCopy}>
          {copied ? (
            <>
              <Check size={12} className={styles.codeCopied} />
              <span className={styles.codeCopied}>Copied</span>
            </>
          ) : (
            <>
              <Copy size={12} />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className={styles.codePre}>
        <code>{code}</code>
      </pre>
    </div>
  );
};

export const InfoBox = ({ type = "info", title, children, spaced, icon: IconOverride }) => {
  const variant = INFO_BOX_VARIANTS[type] ?? INFO_BOX_VARIANTS.info;
  const Icon = IconOverride ?? variant.icon;
  return (
    <div className={`${styles.infoBox} ${variant.boxClass} ${spaced ? styles.infoBoxSpaced : ""}`}>
      <Icon size={14} className={`${styles.infoBoxIcon} ${variant.iconClass}`} />
      <div className={styles.infoBoxBody}>
        {title && <Strong>{`${title} `}</Strong>}
        {children}
      </div>
    </div>
  );
};

export const H2 = ({ id, children }) => (
  <h2 id={id} className={styles.h2}>{children}</h2>
);

export const H3 = ({ id, children }) => (
  <h3 id={id} className={styles.h3}>{children}</h3>
);

export const H4 = ({ children }) => (
  <h4 className={styles.h4}>{children}</h4>
);

export const P = ({ children }) => (
  <p className={styles.p}>{children}</p>
);

export const Li = ({ children }) => (
  <li className={styles.li}>
    <ChevronRight size={14} className={styles.liIcon} />
    <span className={styles.liText}>{children}</span>
  </li>
);

export const Ul = ({ children }) => (
  <ul className={styles.ul}>{children}</ul>
);

export const StepList = ({ steps }) => (
  <ol className={styles.stepList}>
    {steps.map((step, i) => (
      <li key={i} className={styles.stepItem}>
        <span className={styles.stepNumber}>{i + 1}</span>
        <div className={styles.stepBody}>
          {step && typeof step === "object" && step.title ? (
            <>
              <p className={styles.stepTitle}>{step.title}</p>
              {step.desc && <p className={styles.stepDesc}>{step.desc}</p>}
            </>
          ) : (
            <p className={styles.stepDesc}>{step}</p>
          )}
        </div>
      </li>
    ))}
  </ol>
);

export const FeatureCards = ({ items }) => (
  <div className={styles.featureGrid}>
    {items.map(({ icon: Icon, title, desc }) => (
      <div key={title} className={styles.featureCard}>
        <div className={styles.featureIconWrap}>
          <Icon size={16} className={styles.featureIcon} />
        </div>
        <h4 className={styles.featureTitle}>{title}</h4>
        <p className={styles.featureDesc}>{desc}</p>
      </div>
    ))}
  </div>
);

export const Table = ({ headers, rows }) => (
  <div className={styles.tableWrap}>
    <table className={styles.table}>
      <thead>
        <tr className={styles.tableHead}>
          {headers.map((h) => (
            <th key={h} className={styles.tableHeadCell}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={styles.tableRow}>
            {row.map((cell, j) => (
              <td
                key={j}
                className={j === 0 ? `${styles.tableCell} ${styles.tableCellFirst}` : styles.tableCell}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Badge = ({ children, color = "blue" }) => (
  <span className={`${styles.badge} ${BADGE_VARIANTS[color] ?? BADGE_VARIANTS.blue}`}>
    {children}
  </span>
);

export const StatusGrid = ({ items }) => (
  <div className={styles.statusGrid}>
    {items.map(({ label, color, desc }) => (
      <div key={label} className={styles.statusCard}>
        <Badge color={color}>{label}</Badge>
        <p className={styles.statusCardDesc}>{desc}</p>
      </div>
    ))}
  </div>
);

export const PromptCompare = ({ bad, good, badNote }) => (
  <div className={styles.compareGrid}>
    <div className={styles.compareCardBad}>
      <p className={styles.compareLabelBad}>Too vague</p>
      <p className={styles.compareQuote}>{bad}</p>
      {badNote && <p className={styles.compareNote}>{badNote}</p>}
    </div>
    <div className={styles.compareCardGood}>
      <p className={styles.compareLabelGood}>Specific and effective</p>
      <p className={styles.compareQuote}>{good}</p>
    </div>
  </div>
);

export const EndpointRow = ({ method, path, desc }) => (
  <div className={styles.endpointRow}>
    <span className={`${styles.methodBadge} ${METHOD_BADGE_VARIANTS[method] ?? METHOD_BADGE_VARIANTS.GET}`}>
      {method}
    </span>
    <InlineCode>{path}</InlineCode>
    <span className={styles.endpointDesc}>{desc}</span>
  </div>
);

export const ApiEndpointGroup = ({ title, endpoints }) => (
  <div className={styles.apiGroup}>
    <h4 className={styles.apiGroupTitle}>{title}</h4>
    <div className={styles.endpointList}>
      {endpoints.map(({ method, path, desc }) => (
        <EndpointRow key={path} method={method} path={path} desc={desc} />
      ))}
    </div>
  </div>
);

export const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.faqItem}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={styles.faqButton}
      >
        <span className={styles.faqQuestion}>{q}</span>
        <ChevronDown
          size={16}
          className={`${styles.faqChevron} ${open ? styles.faqChevronOpen : ""}`}
        />
      </button>
      {open && (
        <div className={styles.faqAnswer}>
          <p className={styles.faqAnswerText}>{a}</p>
        </div>
      )}
    </div>
  );
};

export const FaqList = ({ items }) => (
  <div className={styles.faqList}>
    {items.map(({ q, a }) => (
      <FaqItem key={q} q={q} a={a} />
    ))}
  </div>
);

const MEDIA_DIMENSION_PRESETS = {
  full: "100%",
};

const resolveMediaDimension = (value) => {
  if (value == null || value === "") {
    return undefined;
  }

  if (typeof value === "number") {
    return `${value}px`;
  }

  if (typeof value === "string") {
    const preset = MEDIA_DIMENSION_PRESETS[value];
    if (preset) {
      return preset;
    }

    if (/^\d+$/.test(value)) {
      return `${value}px`;
    }

    return value;
  }

  return undefined;
};

const buildMediaStyle = (width, height) => {
  const resolvedWidth = resolveMediaDimension(width);
  const resolvedHeight = resolveMediaDimension(height);

  if (!resolvedWidth && !resolvedHeight) {
    return undefined;
  }

  return {
    ...(resolvedWidth ? { width: resolvedWidth } : {}),
    ...(resolvedHeight ? { height: resolvedHeight } : {}),
  };
};

export const DocImage = ({ url, alt = "", caption, width, height }) => {
  const style = buildMediaStyle(width, height);

  return (
    <figure className={styles.mediaFigure}>
      <img
        src={url}
        alt={alt}
        className={styles.mediaImage}
        loading="lazy"
        style={style}
      />
      {caption && <figcaption className={styles.mediaCaption}>{caption}</figcaption>}
    </figure>
  );
};

export const DocVideo = ({ url, caption, width, height }) => {
  const title = caption || "Documentation video";
  const wrapStyle = buildMediaStyle(width, height);
  const hasExplicitHeight = resolveMediaDimension(height) != null;

  return (
    <figure className={styles.mediaFigure}>
      <div
        className={styles.mediaVideoWrap}
        style={{
          ...wrapStyle,
          ...(hasExplicitHeight ? { aspectRatio: "unset" } : {}),
        }}
      >
        <video src={url} controls className={styles.mediaVideo} title={title} />
      </div>
      {caption && <figcaption className={styles.mediaCaption}>{caption}</figcaption>}
    </figure>
  );
};
